import { useEffect, useRef, useState, type ReactNode } from "react";
import { loginService } from "@/api/services/loginService";
import { useUserStore } from "@/stores/react/authStore";
import wxUtils from "@/utils/wx";
import {
    clearEnterpriseWeChatCallback,
    loginWithEnterpriseWeChatCode,
    WX_AUTO_LOGIN_ATTEMPT_KEY
} from "@/views/auth/enterpriseWeChat";

export { WX_AUTO_LOGIN_ATTEMPT_KEY } from "@/views/auth/enterpriseWeChat";

/**
 * 使用本地持久化 Token 向服务端恢复完整用户信息，确保权限守卫使用最新权限列表。
 */
export async function restoreUserSession(): Promise<boolean> {
    const store = useUserStore.getState();
    if (!store.token) {
        return false;
    }

    try {
        const userInfo = await loginService.getUserInfo();
        useUserStore.getState().setUserInfo(userInfo);
        return true;
    } catch {
        // Token 失效时清理本地会话，交由路由守卫回到登录页。
        useUserStore.getState().setToken("");
        return false;
    }
}

export interface AuthBootstrapProps {
    children: ReactNode;
}

/**
 * 应用启动认证门禁：完成用户信息恢复后再创建路由，避免首帧因权限为空误跳 403。
 */
export function AuthBootstrap({ children }: AuthBootstrapProps) {
    const token = useUserStore(state => state.token);
    const [ready, setReady] = useState(false);
    const restoredToken = useRef<string | null>(null);
    const restorePromise = useRef<Promise<boolean> | null>(null);
    const wxLoginStarted = useRef(false);
    const wxCallbackLoginInProgress = useRef(false);
    const isEnterpriseWeChatConfigured = Boolean(import.meta.env.VITE_APP_ID && import.meta.env.VITE_AGENT_ID);

    useEffect(() => {
        let disposed = false;

        const initializeAuth = async () => {
            if (token) {
                // 企微回调流程会主动恢复用户信息，跳过 Token 写入触发的重复恢复请求。
                if (wxCallbackLoginInProgress.current) {
                    return;
                }
                // React StrictMode 开发环境会重复执行 effect，按 Token 去重避免重复请求用户信息。
                if (restoredToken.current === token) {
                    // 复用首次 effect 尚未完成的恢复 Promise，避免 StrictMode 提前放行路由导致 403。
                    if (restorePromise.current) {
                        await restorePromise.current;
                    }
                    if (!disposed) {
                        setReady(true);
                    }
                    return;
                }
                restoredToken.current = token;
                setReady(false);
                const currentRestorePromise = restoreUserSession();
                restorePromise.current = currentRestorePromise;
                await currentRestorePromise;
                if (restorePromise.current === currentRestorePromise) {
                    restorePromise.current = null;
                }
                if (!disposed) {
                    setReady(true);
                }
                return;
            }

            if (!isEnterpriseWeChatConfigured || !wxUtils.isWxWork() || wxLoginStarted.current) {
                if (!disposed) {
                    setReady(true);
                }
                return;
            }

            // 整个页面生命周期只允许启动一次企微授权流程，避免 StrictMode 或状态变化重复发起。
            wxLoginStarted.current = true;
            const callbackCode = wxUtils.getCode();
            if (callbackCode) {
                wxCallbackLoginInProgress.current = true;
                try {
                    const accessToken = await loginWithEnterpriseWeChatCode(callbackCode);
                    restoredToken.current = accessToken;
                    const restored = await restoreUserSession();
                    if (restored) {
                        sessionStorage.removeItem(WX_AUTO_LOGIN_ATTEMPT_KEY);
                        clearEnterpriseWeChatCallback({ navigateHome: true });
                    }
                } catch {
                    // code 兑换失败时保留授权尝试标记并回到登录页，避免失败回调再次触发跳转。
                    useUserStore.getState().setToken("");
                } finally {
                    wxCallbackLoginInProgress.current = false;
                    // Token 写入会触发 effect 清理，但当前认证组件仍在显示，必须解除启动门禁。
                    setReady(true);
                }
                return;
            }

            // 没有 code 通常是首次进入；仅允许一次跳转，异常回调会停在登录页而不再循环。
            if (!sessionStorage.getItem(WX_AUTO_LOGIN_ATTEMPT_KEY)) {
                sessionStorage.setItem(WX_AUTO_LOGIN_ATTEMPT_KEY, "1");
                wxUtils.goAutoLogin();
                return;
            }

            if (!disposed) {
                setReady(true);
            }
        };

        void initializeAuth();
        return () => {
            disposed = true;
        };
    }, [isEnterpriseWeChatConfigured, token]);

    if (!ready) {
        // 恢复会话期间保持页面空白，避免刷新瞬间展示临时状态文案。
        return null;
    }

    return <>{children}</>;
}
